import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import { useUsers } from '../../hooks/useUsers';
import Modal from '../shared/Modal';
import './NewChannelModal.css';

const AVATAR_COLORS = [
  '#4d94f8', '#34d399', '#f472b6', '#fbbf24', '#a78bfa',
  '#fb923c', '#38bdf8', '#f87171', '#818cf8', '#2dd4bf',
];

function getAvatarColor(name) {
  const code = (name || 'A').charCodeAt(0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

function NewChannelModal({ onClose }) {
  const { createChannel, conversations } = useContext(WorkspaceContext);
  const { users } = useUsers();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [query, setQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [error, setError] = useState('');
  const memberInputRef = useRef(null);

  const handleNameChange = (e) => {
    const val = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '');
    setName(val);
    setError('');
  };

  const filteredUsers = useMemo(() => {
    const nonAgents = users.filter((u) => !u.isAgent);
    const notSelected = nonAgents.filter((u) => !selectedMembers.some((m) => m.username === u.username));
    if (!query.trim()) return notSelected;
    const q = query.toLowerCase();
    return notSelected.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q)
    );
  }, [query, users, selectedMembers]);

  const handleAddMember = useCallback((user) => {
    setSelectedMembers((prev) => [...prev, user]);
    setQuery('');
    setShowDropdown(false);
    setHighlightIndex(0);
    requestAnimationFrame(() => memberInputRef.current?.focus());
  }, []);

  const handleRemoveMember = useCallback((username) => {
    setSelectedMembers((prev) => prev.filter((m) => m.username !== username));
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (!showDropdown || filteredUsers.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % filteredUsers.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + filteredUsers.length) % filteredUsers.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleAddMember(filteredUsers[highlightIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  }, [showDropdown, filteredUsers, highlightIndex, handleAddMember]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const normalized = trimmed.toLowerCase();

    const hasDuplicate = conversations.some(
      (c) => c.type === 'CHANNEL' && (c.title || '').trim().toLowerCase() === normalized
    );
    if (hasDuplicate) {
      setError('A shared chat with this name already exists.');
      return;
    }

    try {
      await createChannel(
        trimmed,
        description.trim() || null,
        selectedMembers.map((m) => m.username)
      );
      onClose();
    } catch {
      setError('Failed to create shared chat. Please try again.');
    }
  };

  return (
    <Modal title="New Channel" onClose={onClose}>
      <form className="ncm-form" onSubmit={handleSubmit}>
        {/* Channel name */}
        <div className="ncm-field">
          <label className="ncm-label">Channel name</label>
          <div className="ncm-name-input-wrap">
            <span className="ncm-name-prefix">#</span>
            <input
              className="ncm-name-input"
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="e.g. general"
              autoFocus
            />
          </div>
          <span className="ncm-hint">Lowercase, no spaces</span>
        </div>

        {/* Description */}
        <div className="ncm-field">
          <label className="ncm-label">
            Description <span className="ncm-optional">(optional)</span>
          </label>
          <textarea
            className="ncm-textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What's this channel about?"
          />
        </div>

        {/* Add members */}
        <div className="ncm-field">
          <label className="ncm-label">
            Add members <span className="ncm-optional">(optional)</span>
          </label>

          {/* Selected member chips */}
          {selectedMembers.length > 0 && (
            <div className="ncm-chips">
              {selectedMembers.map((m) => (
                <span key={m.username} className="ncm-chip">
                  {m.displayName}
                  <button
                    type="button"
                    className="ncm-chip-x"
                    onClick={() => handleRemoveMember(m.username)}
                    aria-label={`Remove ${m.displayName}`}
                  >
                    <svg viewBox="0 0 12 12" width="10" height="10" fill="currentColor">
                      <path d="M3.05 3.05a.5.5 0 01.7 0L6 5.29l2.25-2.24a.5.5 0 01.7.7L6.71 6l2.24 2.25a.5.5 0 01-.7.7L6 6.71 3.75 8.95a.5.5 0 01-.7-.7L5.29 6 3.05 3.75a.5.5 0 010-.7z" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Member search */}
          <div className="ncm-member-search-wrap">
            <svg className="ncm-search-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
            </svg>
            <input
              ref={memberInputRef}
              className="ncm-member-search"
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowDropdown(true);
                setHighlightIndex(0);
                setError('');
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              onKeyDown={handleKeyDown}
              placeholder="Search by name or email..."
            />
          </div>

          {/* Dropdown user list */}
          {showDropdown && (
            <div className="ncm-user-list">
              {filteredUsers.length === 0 ? (
                <div className="ncm-empty">No users found</div>
              ) : (
                filteredUsers.map((user, idx) => {
                  const initial = (user.displayName || user.username).charAt(0).toUpperCase();
                  return (
                    <button
                      key={user.username}
                      type="button"
                      className={`ncm-user-item${idx === highlightIndex ? ' ncm-highlighted' : ''}`}
                      onMouseEnter={() => setHighlightIndex(idx)}
                      onMouseDown={(e) => { e.preventDefault(); handleAddMember(user); }}
                    >
                      <span
                        className="ncm-avatar"
                        style={{ backgroundColor: getAvatarColor(user.displayName || user.username) }}
                      >
                        {initial}
                      </span>
                      <div className="ncm-user-info">
                        <span className="ncm-user-name">{user.displayName}</span>
                        <span className="ncm-user-email">{user.username}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Error */}
        {error && <p className="ncm-error">{error}</p>}

        {/* Submit */}
        <button
          type="submit"
          className="ncm-submit"
          disabled={!name.trim()}
        >
          Create Channel
        </button>
      </form>
    </Modal>
  );
}

export default NewChannelModal;
